import instance from "api/apiClient";
import Pagination from "components/Pagination";
import { gameCenterLimits } from "configs/limits";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { formatTime, useTimeFormat } from "helpers/timeFormat";
import "./TrailTable.scss";
import ModalTabSkeleton from "components/global/Skeleton/ModalTabSkeleton/ModalTabSkeleton";

interface Props {
  entityType: string;
  entityId: number;
}

interface Actor {
  id: string;
  role: string;
  nickname: string;
  email: string;
  firstName: string;
}

interface TrailItem {
  hash: string;
  ts: string;
  action: "create" | "update" | "bulkUpdate" | "delete" | string;
  actor: Actor;
  message: string;
}

const ACTION_LABEL_MAP: Record<string, { label: string; className: string }> = {
  create: {
    label: "Create",
    className: "trail-label trail-label--success",
  },
  add: {
    label: "Add",
    className: "trail-label trail-label--success",
  },
  remove: {
    label: "Remove",
    className: "trail-label trail-label--danger",
  },
  update: {
    label: "Update",
    className: "trail-label trail-label--warning",
  },
  bulkUpdate: {
    label: "Update | Bulk",
    className: "trail-label trail-label--warning2",
  },
  priorityUpdate: {
    label: "Update | Priority",
    className: "trail-label trail-label--warning",
  },
  delete: {
    label: "Delete",
    className: "trail-label trail-label--danger",
  },
};

const TrailTable: React.FC<Props> = ({ entityType, entityId }) => {
  const timeFormat = useTimeFormat();
  const { t } = useTranslation();

  const [items, setItems] = useState<TrailItem[]>([]);
  const [activePage, setActivePage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [currentLimit, setCurrentLimit] = useState(gameCenterLimits[0]);

  const offset = activePage - 1;

  useEffect(() => {
    if (!entityId) return;

    const fetchTrail = async () => {
      setIsLoading(true);

      try {
        const res = await instance.get(`/log/activity/trail`, {
          params: {
            entityType,
            entityId,
            limit: currentLimit,
            offset,
          },
        });

        setItems(res.data.items || []);
        setTotalItems(res.data.totalItems || 0);
        setTotalPages(res.data.totalPages || 0);
      } catch (e) {
        console.log("Failed to load trail", e);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTrail();
  }, [entityId, activePage, currentLimit]);

  const handlePrevClick = () => {
    if (activePage > 1) {
      setActivePage((p) => p - 1);
    }
  };

  const handleNextClick = () => {
    if (activePage < totalPages) {
      setActivePage((p) => p + 1);
    }
  };

  return (
    <div className="trail-wrapper">
      <div className="inner-table-wrapper">
        {isLoading ? (
          <ModalTabSkeleton />
        ) : (
          <>
            <table>
              <thead>
                <tr>
                  <th>{t("date")}</th>
                  <th>{t("action")}</th>
                  <th className="trail-activity">{t("activity")}</th>
                  <th className="trail-hash">{t("hash")}</th>
                </tr>
              </thead>

              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={4} style={{ textAlign: "center" }}>
                      No activity yet
                    </td>
                  </tr>
                ) : (
                  items.map((el) => (
                    <tr key={el.hash}>
                      <td className="date">
                        <span>{formatTime(el.ts, timeFormat, true)}</span>
                        <span>{moment(el.ts).format("DD.MM.YYYY")}</span>
                      </td>

                      <td>
                        {(() => {
                          const actionConfig = ACTION_LABEL_MAP[el.action];

                          if (!actionConfig) {
                            return el.action;
                          }

                          return (
                            <span className={actionConfig.className}>
                              {actionConfig.label}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="big-text">{el.message}</td>
                      <td className="trail-hash">
                        <span title={el.hash}>{el.hash.slice(0, 16)}...</span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </>
        )}
      </div>

      {items.length > 0 && (
        <div className="trail-pagination">
          <Pagination
            activePage={activePage}
            limit={currentLimit}
            totalItems={totalItems}
            totalPages={totalPages}
            limitsArray={gameCenterLimits}
            handleLimitClick={(limit) => {
              setCurrentLimit(limit);
              setActivePage(1);
            }}
            handleNextClick={handleNextClick}
            handlePrevClick={handlePrevClick}
          />
        </div>
      )}
    </div>
  );
};

export default TrailTable;
